import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit'
import { createReduxActionDefinition } from 'app/state/action'
import { createResource } from 'app/state/resource'
import { ApiError, RequestStatus } from 'features/common'
import { UserRole } from 'features/users'

import { Account, Users, AccountStats, UsersListFilter, LdsQuotas, ResourceListFilters } from '../types'
import { ListOrder, FieldToOrderBy, SortDirection, ResourceType } from 'features/common/types'
import { getFeatureFlag } from 'features/common/state/commonSlice'

const name = 'account'

export interface AccountState {
  // data
  data: Account | Record<string, never>
  dataStatus: RequestStatus
  dataError?: ApiError | null

  // invitation
  invitation: Record<string, never>
  invitationStatus: RequestStatus
  invitationError?: ApiError | null

  deleteUser: Record<string, never>
  users: Users[]
  filteredUsers: Array<Users>
  usersFilter: UsersListFilter
  usersOrder: ListOrder

  // renameAccount
  renameAccountStatus: RequestStatus
  renameAccountError?: ApiError | null

  // stats
  statsData: AccountStats | Record<string, never>
  statsStatus: RequestStatus
  statsError?: ApiError | null

  // requiresJoinApproval
  requiresJoinApprovalStatus: RequestStatus
  requiresJoinApprovalError?: ApiError | null
  isDomainJoinable?: boolean
  isDomainJoinableStatus?: RequestStatus
  isDomainJoinableError?: ApiError | null

  // deleteAccount
  deleteAccountStatus: RequestStatus
  deleteAccountError?: ApiError | null

  // ldsQuotas
  ldsQuotasStats: LdsQuotas[]
  ldsQuotasStatsStatus: RequestStatus
  ldsQuotasStatsError?: ApiError | null

  // settings
  settings: {
    assetManagement: {
      tabs: {
        map: {
          filters: ResourceListFilters
        }
        connection: {
          filters: ResourceListFilters
        }
        workflow: {
          filters: ResourceListFilters
        }
      }
    }
  }
}

const initialState: AccountState = {
  // data
  data: {},
  dataStatus: RequestStatus.Idle,

  // invitation
  invitation: {},
  invitationStatus: RequestStatus.Idle,

  deleteUser: {},
  users: [],
  filteredUsers: [],
  usersFilter: {},
  usersOrder: {
    by: FieldToOrderBy.CreatedAt,
    direction: SortDirection.Desc
  },

  // renameAccount
  renameAccountStatus: RequestStatus.Idle,

  // stats
  statsData: {},
  statsStatus: RequestStatus.Idle,

  requiresJoinApprovalStatus: RequestStatus.Idle,

  deleteAccountStatus: RequestStatus.Idle,

  ldsQuotasStats: [],
  ldsQuotasStatsStatus: RequestStatus.Idle,

  settings: {
    assetManagement: {
      tabs: {
        map: {
          filters: {
            resourceType: ResourceType.Map,
            pageSize: 10,
            page: 1,
            q: '',
            orderBy: FieldToOrderBy.updatedAt,
            orderDirection: SortDirection.Desc
          }
        },
        workflow: {
          filters: {
            resourceType: ResourceType.Workflow,
            pageSize: 10,
            page: 1,
            q: '',
            orderBy: FieldToOrderBy.updatedAt,
            orderDirection: SortDirection.Desc
          }
        },
        connection: {
          filters: {
            resourceType: ResourceType.Connection,
            pageSize: 10,
            page: 1,
            q: '',
            orderBy: FieldToOrderBy.updatedAt,
            orderDirection: SortDirection.Desc
          }
        }
      }
    }
  }
}

export interface DeleteUserAction {
  userId: string
  resourcesReceiverUserId?: string
}
export interface AccountAction {
  type: string
  payload: DeleteUserAction
}

export interface InvitationAction {
  email: string[] | string
  role: UserRole
}

export interface RequiresJoinApprovalAction {
  accountName: string
  emailDomain: string
  action: boolean
}

export interface UserRoleAction {
  userId: string
  role: UserRole
  resourcesReceiverUserId?: string
}

const asyncActions = {
  accountRequest: createAction(`${name}/account_request`),
  renameAccountRequest: createAction<string>(`${name}/account_rename_request`),
  invitationRequest: createAction<InvitationAction>(`${name}/invitation_request`),
  deleteUserRequest: createAction<string>(`${name}/delete_user_request`),
  usersRequest: createAction(`${name}/users_request`),
  statsRequest: createAction(`${name}/stats_request`),
  requiresJoinApprovalRequest: createAction<RequiresJoinApprovalAction>(`${name}/requires_join_approval_request`),
  isDomainJoinableRequest: createAction<string>(`${name}/is_domain_joinable`),
  deleteAccountRequest: createAction<string>(`${name}/delete_account_request`),
  checkLdsQuotasStatsRequest: createAction(`${name}/check_lds_quotas_stats_request`)
}

const reducers = {
  // data
  setAccountData: (state: AccountState, action: PayloadAction<Account>) => {
    state.data = action.payload
  },
  updateAccountData: (state: AccountState, action: PayloadAction<Partial<Account>>) => {
    state.data = { ...(state.data as Account), ...action.payload }
  },
  setAccountDataStatus: (state: AccountState, action: PayloadAction<RequestStatus>) => {
    state.dataStatus = action.payload
  },
  setAccountDataError: (state: AccountState, action: PayloadAction<ApiError | null>) => {
    state.dataError = action.payload
  },

  // invitation
  setInvitation: (state: AccountState, action: PayloadAction<Record<string, never>>) => {
    state.invitation = action.payload
  },
  setAccountInvitationStatus: (state: AccountState, action: PayloadAction<RequestStatus>) => {
    state.invitationStatus = action.payload
  },
  setAccountInvitationError: (state: AccountState, action: PayloadAction<ApiError | null>) => {
    state.invitationError = action.payload
  },

  setFilteredUsers: (state: AccountState, action: PayloadAction<Array<Users>>) => {
    state.filteredUsers = action.payload
  },
  setUsersListFilter: (state: AccountState, action: PayloadAction<UsersListFilter>) => {
    state.usersFilter = action.payload
  },
  resetUsersListFilter: (state: AccountState) => {
    state.usersFilter = { searchBy: '' }
  },
  setUsersListOrder: (state: AccountState, action: PayloadAction<ListOrder>) => {
    state.usersOrder = action.payload
  },
  setDeleteUser: (state: AccountState, action: PayloadAction<Record<string, never>>) => {
    state.deleteUser = action.payload
  },
  setUsers: (state: AccountState, action: PayloadAction<Array<Users>>) => {
    state.users = action.payload
  },

  // renameAccount
  setRenameAccountStatus: (state: AccountState, action: PayloadAction<RequestStatus>) => {
    state.renameAccountStatus = action.payload
  },
  setRenameAccountError: (state: AccountState, action: PayloadAction<ApiError | null>) => {
    state.renameAccountError = action.payload
  },

  // stats
  setStatsData: (state: AccountState, action: PayloadAction<AccountStats>) => {
    state.statsData = action.payload
  },
  setStatsStatus: (state: AccountState, action: PayloadAction<RequestStatus>) => {
    state.statsStatus = action.payload
  },
  setStatsError: (state: AccountState, action: PayloadAction<ApiError | null>) => {
    state.statsError = action.payload
  },

  // requiresJoinApproval
  setRequiresJoinApprovalStatus: (state: AccountState, action: PayloadAction<RequestStatus>) => {
    state.requiresJoinApprovalStatus = action.payload
  },
  setRequiresJoinApprovalError: (state: AccountState, action: PayloadAction<ApiError | null>) => {
    state.requiresJoinApprovalError = action.payload
  },
  setIsDomainJoinable: (state: AccountState, action: PayloadAction<boolean>) => {
    state.isDomainJoinable = action.payload
  },
  setIsDomainJoinableStatus: (state: AccountState, action: PayloadAction<RequestStatus>) => {
    state.isDomainJoinableStatus = action.payload
  },
  setIsDomainJoinableError: (state: AccountState, action: PayloadAction<ApiError | null>) => {
    state.isDomainJoinableError = action.payload
  },

  // deleteAccount
  setDeleteAccountStatus: (state: AccountState, action: PayloadAction<RequestStatus>) => {
    state.deleteAccountStatus = action.payload
  },
  setDeleteAccountError: (state: AccountState, action: PayloadAction<ApiError | null>) => {
    state.deleteAccountError = action.payload
  },

  setLdsQuotasStats: (state: AccountState, action: PayloadAction<LdsQuotas[]>) => {
    state.ldsQuotasStats = action.payload
  },
  setLdsQuotasStatsStatus: (state: AccountState, action: PayloadAction<RequestStatus>) => {
    state.ldsQuotasStatsStatus = action.payload
  },
  setLdsQuotasStatsError: (state: AccountState, action: PayloadAction<ApiError | null>) => {
    state.ldsQuotasStatsError = action.payload
  },
  setAssetManagementTabFilters: (state: AccountState, action: PayloadAction<ResourceListFilters>) => {
    state.settings.assetManagement.tabs[action.payload.resourceType].filters = action.payload
  }
}

export const getFlag = getFeatureFlag

export const accountResource = createResource({
  get: (state) => state.account.data,
  status: (state) => state.account.dataStatus,
  error: (state) => state.account.dataError,
  createAction: () => asyncActions.accountRequest()
})

export const statsResource = createResource({
  get: (state) => state.account.statsData?.stats,
  status: (state) => state.account.statsStatus,
  error: (state) => state.account.statsError,
  createAction: () => asyncActions.statsRequest()
})

export const invitationActionDefinition = createReduxActionDefinition<InvitationAction>(
  (dispatch, payload) => dispatch(invitationRequest(payload)),
  {
    status: (state) => state.account.invitationStatus,
    error: (state) => state.account.invitationError,
    dismissResult: (dispatch) => dispatch(setAccountInvitationStatus(RequestStatus.Idle))
  }
)

export const accountSlice = createSlice({
  name,
  initialState,
  reducers
})

export const {
  // account
  setAccountData,
  updateAccountData,
  setAccountDataStatus,
  setAccountDataError,
  // invitation
  setInvitation,
  setAccountInvitationStatus,
  setAccountInvitationError,
  setDeleteUser,
  setUsers,
  setFilteredUsers,
  setUsersListFilter,
  resetUsersListFilter,
  setUsersListOrder,
  // renameAccount
  setRenameAccountStatus,
  setRenameAccountError,
  // stats
  setStatsData,
  setStatsStatus,
  setStatsError,
  // requiresJoinApproval
  setRequiresJoinApprovalStatus,
  setRequiresJoinApprovalError,
  setIsDomainJoinable,
  setIsDomainJoinableStatus,
  setIsDomainJoinableError,
  // deleteAccount
  setDeleteAccountStatus,
  setDeleteAccountError,

  setLdsQuotasStats,
  setLdsQuotasStatsStatus,
  setLdsQuotasStatsError,
  setAssetManagementTabFilters
} = accountSlice.actions

export const {
  accountRequest,
  invitationRequest,
  deleteUserRequest,
  usersRequest,
  renameAccountRequest,
  statsRequest,
  requiresJoinApprovalRequest,
  isDomainJoinableRequest,
  deleteAccountRequest,
  checkLdsQuotasStatsRequest
} = asyncActions

export default accountSlice.reducer
